import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { TranslationService } from '@trimble-gcs/i18n';
import { ModusButtonModule } from '@trimble-gcs/modus';
import { catchError, EMPTY, finalize, repeat, Subject, switchMap } from 'rxjs';
import { isApiErrorType } from '../../error-handling/api-error-type';
import { LoadingService } from '../../loading/loading.service';
import { isRegionNotFoundError } from '../../tenancy/region-not-found.error';
import { LicenseService } from '../license.service';
import { LicenseState } from '../license.state';
import { licenseCheckTranslations } from './license-check.translations';

enum LicenseView {
  LoadLicense,
  NoLicense,
  AccountNotAllowed,
  RegionNotAvailable,
  UnknownError,
}

@Component({
  imports: [CommonModule, MatProgressBarModule, ModusButtonModule],
  templateUrl: './license-check.component.html',
  styles: [
    `
      :host {
        height: 100%;
        background-color: white;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LicenseCheckComponent {
  private readonly loadingService = inject(LoadingService);
  private readonly licenseService = inject(LicenseService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly translationService = inject(TranslationService);

  readonly translations = licenseCheckTranslations();
  readonly view = LicenseView;

  readonly isLoading = toSignal(this.loadingService.isLoading$(this), { initialValue: false });
  readonly currentView = signal(LicenseView.LoadLicense);

  private readonly reloadLicenses$ = new Subject<void>();

  readonly regionNotAvailableMessage = computed(() =>
    this.translationService.translate(this.translations.regionNotAvailable.message.text, {
      'Reality Capture': 'Reality Capture',
    }),
  );

  constructor() {
    this.loadLicenses();
  }

  reloadLicense() {
    this.reloadLicenses$.next();
  }

  private loadLicenses() {
    this.loadingService
      .loadFrom(this.licenseService.loadLicenses(), this)
      .pipe(
        catchError((err: unknown) => this.handleError(err)),
        switchMap(() => this.store.selectOnce(LicenseState.hasActiveLicense)),
        finalize(() => this.loadingService.stopLoading(this)),
        repeat({ delay: () => this.reloadLicenses$ }),
      )
      .subscribe((hasActiveLicense) => {
        if (hasActiveLicense) {
          this.navigateToReturnPath();
        } else {
          this.currentView.set(LicenseView.NoLicense);
        }
      });
  }

  private handleError(err: unknown) {
    let view: LicenseView;

    if (isRegionNotFoundError(err)) {
      view = LicenseView.RegionNotAvailable;
    } else if (isApiErrorType(err as HttpErrorResponse, 'ACCOUNT_NOT_ALLOWED')) {
      view = LicenseView.AccountNotAllowed;
    } else {
      view = LicenseView.UnknownError;
    }

    this.currentView.set(view);
    return EMPTY;
  }

  private navigateToReturnPath() {
    const returnPath = this.activatedRoute.snapshot.queryParamMap.get('returnPath') ?? '';
    this.router.navigate([returnPath]);
  }
}
